import { useCallback, useEffect, useRef, useState } from 'react';
import { CELL, TIMING } from './constants';
import { boardToPieces, newPieceId } from './boardModel';
import { impact, notify, selection } from '../common/haptics';
import { msg } from '../../../i18n/msg';

/**
 * Sequential playback of Match-3 server events on a list of pieces (stable ids → CSS transitions).
 *
 * reset(board)                       — show a server board (ids reused where the piece did not change)
 * play(events, {score, movesLeft}, onDone) — animate the events from the current pieces; `onDone` is called
 *                                      right before playback ends so the caller can apply the final state.
 * invalid(a, b)                      — "try and bounce back" animation of a rejected swap.
 * optimisticSwap(a, b) / revertSwap(a, b) — move the two gems right away, before the server answers;
 *                                      then play(events, …, { swapDone: true }) skips the server's own swap step.
 * While `playing` the page must block input and show `score` / `movesLeft` from the hook.
 */
export default function useMatch3Playback() {
  const [pieces, setPieces] = useState([]);
  const [effects, setEffects] = useState([]);
  const [banner, setBanner] = useState(null);
  const [score, setScore] = useState(null);
  const [movesLeft, setMovesLeft] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [cheer, setCheer] = useState(0);

  const piecesRef = useRef([]);
  const timersRef = useRef(new Set());
  const seqRef = useRef(0);
  const movesRef = useRef(null);

  const later = useCallback((fn, ms) => {
    const t = setTimeout(() => {
      timersRef.current.delete(t);
      fn();
    }, ms);
    timersRef.current.add(t);
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current.clear();
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const commit = useCallback((next) => {
    piecesRef.current = next;
    setPieces(next);
  }, []);

  const reset = useCallback((board) => {
    commit(boardToPieces(board, piecesRef.current));
  }, [commit]);

  const nonce = () => {
    seqRef.current += 1;
    return seqRef.current;
  };

  const addEffect = useCallback((effect, life) => {
    const id = `fx${nonce()}`;
    setEffects((prev) => [...prev, { ...effect, id }]);
    later(() => setEffects((prev) => prev.filter((e) => e.id !== id)), life);
  }, [later]);

  const prune = (list) => list.filter((p) => !p.pop);

  /** Applies one event, returns how long to wait before the next one (ms). */
  const runStep = useCallback((ev) => {
    const v = piecesRef.current;
    switch (ev.e) {
      case 'swap': {
        const [ax, ay] = ev.a || [];
        const [bx, by] = ev.b || [];
        commit(prune(v).map((p) => {
          if (p.x === ax && p.y === ay) return { ...p, x: bx, y: by, dur: TIMING.swap, drop: 0, born: false };
          if (p.x === bx && p.y === by) return { ...p, x: ax, y: ay, dur: TIMING.swap, drop: 0, born: false };
          return p;
        }));
        if (movesRef.current != null) {
          movesRef.current = Math.max(0, movesRef.current - 1);
          setMovesLeft(movesRef.current);
        }
        selection();
        return TIMING.swap + 30;
      }
      case 'special': {
        addEffect({ kind: 'special', special: ev.kind, at: ev.at || [0, 0], cells: ev.cells || [], gem: ev.gem, n: nonce() }, TIMING.special + 260);
        impact('heavy');
        return TIMING.special;
      }
      case 'clear': {
        const cells = ev.cells || [];
        const hit = new Set(cells.map((c) => `${c[0]},${c[1]}`));
        let next = prune(v).map((p) => (hit.has(`${p.x},${p.y}`) ? { ...p, pop: true } : p));
        (ev.specials || []).forEach((s) => {
          next = [...next, { id: newPieceId(), x: s.x, y: s.y, gem: s.gem, special: s.special, born: true }];
        });
        commit(next);
        later(() => commit(prune(piecesRef.current)), TIMING.clear - 10);
        cells.forEach((c, i) => {
          if (i < 24) addEffect({ kind: 'spark', x: c[0], y: c[1], n: nonce() }, 600);
        });
        if (cells.length) {
          const cx = cells.reduce((s, c) => s + c[0], 0) / cells.length;
          const cy = cells.reduce((s, c) => s + c[1], 0) / cells.length;
          addEffect({ kind: 'float', x: cx, y: cy, text: `+${ev.points ?? 0}`, combo: ev.combo || 1, n: nonce() }, TIMING.float);
        }
        if (typeof ev.score === 'number') setScore(ev.score);
        const combo = ev.combo || 1;
        if (combo >= 2) {
          setBanner({ text: msg('match3.combo', 'Combo ×{{n}}!', { n: combo }), combo, n: nonce() });
          later(() => setBanner(null), TIMING.comboBanner);
        }
        if (combo >= 3 || (ev.specials || []).length > 0 || cells.length >= 12) setCheer((c) => c + 1);
        impact(combo >= 3 ? 'medium' : 'light');
        return TIMING.clear;
      }
      case 'fall': {
        const moves = ev.moves || [];
        const map = new Map(moves.map((m) => [`${m[0]},${m[1]}`, m[2]]));
        let longest = 0;
        commit(prune(v).map((p) => {
          const to = map.get(`${p.x},${p.y}`);
          if (to == null) return p;
          const dist = Math.abs(to - p.y);
          const dur = Math.min(TIMING.fallMax, TIMING.fallBase + TIMING.fallPerCell * dist);
          longest = Math.max(longest, dur);
          return { ...p, y: to, dur, drop: 0, born: false };
        }));
        // spawns start while the column is still falling
        return Math.round(longest * 0.55);
      }
      case 'spawn': {
        const cells = ev.cells || [];
        const perColumn = {};
        cells.forEach((c) => { perColumn[c[0]] = (perColumn[c[0]] || 0) + 1; });
        const born = cells.map((c) => {
          const k = perColumn[c[0]] || 1;
          const dur = Math.min(TIMING.fallMax, TIMING.fallBase + TIMING.fallPerCell * k);
          return { id: newPieceId(), x: c[0], y: c[1], gem: c[2], special: null, drop: k * CELL, dur };
        });
        commit([...prune(v), ...born]);
        const longest = born.reduce((m, p) => Math.max(m, p.dur), 0);
        return Math.max(TIMING.spawn, longest);
      }
      case 'shuffle': {
        commit(boardToPieces(ev.board || [], []).map((p) => ({ ...p, shuffled: true })));
        addEffect({ kind: 'shuffle', n: nonce() }, TIMING.shuffle);
        setBanner({ text: msg('match3.shuffle', 'No moves — shuffle!'), combo: 0, n: nonce() });
        later(() => setBanner(null), TIMING.shuffle);
        impact('medium');
        return TIMING.shuffle;
      }
      case 'end': {
        const win = ev.result === 'WIN';
        if (typeof ev.score === 'number') setScore(ev.score);
        setBanner({
          text: win ? msg('match3.banner.cleared', 'Mine cleared!') : msg('match3.banner.out_of_moves', 'Out of moves'),
          combo: 0,
          end: win ? 'win' : 'lose',
          n: nonce(),
        });
        notify(win ? 'success' : 'error');
        if (win) setCheer((c) => c + 1);
        return TIMING.end;
      }
      case 'timeout': {
        setBanner({ text: msg('game.banner.times_up', "Time's up!"), combo: 0, end: 'lose', n: nonce() });
        notify('warning');
        return TIMING.timeout;
      }
      default:
        return 0;
    }
  }, [addEffect, commit, later]);

  const swapPieces = useCallback((a, b) => {
    commit(prune(piecesRef.current).map((p) => {
      if (p.x === a[0] && p.y === a[1]) return { ...p, x: b[0], y: b[1], dur: TIMING.swap, drop: 0, born: false };
      if (p.x === b[0] && p.y === b[1]) return { ...p, x: a[0], y: a[1], dur: TIMING.swap, drop: 0, born: false };
      return p;
    }));
  }, [commit]);

  /** Swap is shown immediately — the server round trip hides behind the animation. */
  const optimisticSwap = useCallback((a, b) => {
    swapPieces(a, b);
    selection();
  }, [swapPieces]);

  /** The server rejected an optimistic swap: put the gems back with a wiggle. */
  const revertSwap = useCallback((a, b) => {
    swapPieces(b, a);
    notify('error');
  }, [swapPieces]);

  const play = useCallback((events, start, onDone, opts = {}) => new Promise((resolve) => {
    clearTimers();
    setEffects([]);
    let steps = (events || []).filter((ev) => ev && ev.e);
    let startMoves = start?.movesLeft ?? null;
    if (opts.swapDone) {
      // the gems were already swapped by optimisticSwap — skip the server's swap step, keep its move cost
      const k = steps.findIndex((ev) => ev.e === 'swap');
      if (k >= 0) {
        steps = steps.filter((_, i) => i !== k);
        if (startMoves != null) startMoves = Math.max(0, startMoves - 1);
      }
    }
    const finish = () => {
      if (onDone) onDone();
      setBanner(null);
      setScore(null);
      setMovesLeft(null);
      movesRef.current = null;
      setPlaying(false);
      resolve();
    };
    if (!steps.length) {
      finish();
      return;
    }
    movesRef.current = startMoves;
    setMovesLeft(movesRef.current);
    setScore(start?.score ?? null);
    setPlaying(true);
    let i = 0;
    const next = () => {
      if (i >= steps.length) {
        finish();
        return;
      }
      const step = steps[i];
      i += 1;
      const dur = runStep(step);
      if (dur > 0) later(next, dur);
      else next();
    };
    next();
  }), [clearTimers, later, runStep]);

  /** Rejected swap: pieces lean towards each other and bounce back. */
  const invalid = useCallback((a, b) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const n = nonce();
    commit(piecesRef.current.map((p) => {
      if (p.x === a[0] && p.y === a[1]) return { ...p, wiggle: { dx, dy, n } };
      if (p.x === b[0] && p.y === b[1]) return { ...p, wiggle: { dx: -dx, dy: -dy, n } };
      return p;
    }));
    notify('error');
    later(() => commit(piecesRef.current.map((p) => (p.wiggle && p.wiggle.n === n ? { ...p, wiggle: null } : p))), TIMING.invalid);
  }, [commit, later]);

  return { pieces, effects, banner, score, movesLeft, playing, cheer, play, reset, invalid, optimisticSwap, revertSwap };
}
